import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Video, 
  Link2, 
  Send, 
  Check, 
  AlertCircle, 
  Loader2, 
  ExternalLink,
  Clock,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * ZoomLinkEntry Component
 * 
 * Admin component for adding Zoom links to scheduled consultations.
 * Used in ConsultationDetail page.
 */
export function ZoomLinkEntry({ 
  consultationId,
  currentZoomUrl,
  currentZoomPassword,
  zoomEmailSentAt,
  zoomEmailSentCount = 0,
  status,
  customerEmail,
  customerName,
  scheduledTime,
  onUpdate,
  sessionToken
}) {
  const [zoomUrl, setZoomUrl] = useState(currentZoomUrl || '');
  const [zoomPassword, setZoomPassword] = useState(currentZoomPassword || '');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const hasZoomLink = !!currentZoomUrl;
  const needsZoomLink = status === 'scheduled_pending_zoom' || (status === 'scheduled' && !currentZoomUrl);

  // Validate Zoom URL
  const isValidZoomUrl = (url) => {
    return /^https:\/\/([\w-]+\.)?zoom\.us\/j\/\d+/.test(url);
  };

  // Save Zoom link
  const handleSaveZoomLink = async () => {
    if (!zoomUrl) {
      setError('Please enter a Zoom URL');
      return;
    }

    if (!isValidZoomUrl(zoomUrl)) {
      setError('Invalid Zoom URL format. Expected: https://zoom.us/j/MEETING_ID');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(
        `${API_BASE}/api/v1/admin/consultations/${consultationId}/zoom-link`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionToken}`
          },
          body: JSON.stringify({
            zoom_url: zoomUrl,
            zoom_password: zoomPassword || null
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save Zoom link');
      }

      toast.success('Zoom link saved successfully');
      
      if (onUpdate) {
        onUpdate({
          zoom_join_url: zoomUrl,
          zoom_password: zoomPassword
        });
      }
    } catch (err) {
      console.error('Error saving Zoom link:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Send Zoom email to customer
  const handleSendZoomEmail = async () => {
    if (!currentZoomUrl) {
      setError('Please save a Zoom link first');
      return;
    }

    try {
      setSending(true);
      setError(null);

      const response = await fetch(
        `${API_BASE}/api/v1/admin/consultations/${consultationId}/send-zoom-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionToken}`
          }
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to send Zoom email');
      }

      const result = await response.json();
      toast.success(`Zoom link sent to ${customerEmail}`);
      
      if (onUpdate) {
        onUpdate({
          status: 'zoom_sent',
          zoom_email_sent_at: new Date().toISOString(),
          zoom_email_sent_count: result.email_count
        });
      }
    } catch (err) {
      console.error('Error sending Zoom email:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-l-4 border-l-blue-500" data-testid="zoom-link-entry">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Video className="h-5 w-5 text-blue-500" />
            <CardTitle className="text-lg">Zoom Meeting Link</CardTitle>
          </div>
          {status === 'zoom_sent' && (
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
              <Check className="h-3 w-3 mr-1" />
              Sent to Customer
            </Badge>
          )}
          {needsZoomLink && !hasZoomLink && (
            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
              <AlertCircle className="h-3 w-3 mr-1" />
              Needs Zoom Link
            </Badge>
          )}
        </div>
        <CardDescription>
          Add the Zoom meeting link for this consultation
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Instructions */}
        {!hasZoomLink && (
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Instructions:</strong>
              <ol className="list-decimal ml-4 mt-2 space-y-1">
                <li>Create a new meeting on <a data-testid="zoom-link-entry-a-1" href="https://zoom.us" target="_blank" rel="noopener noreferrer" className="underline">zoom.us</a></li>
                <li>Copy the meeting URL and paste it below</li>
                <li>Save the link, then click "Send to Customer"</li>
              </ol>
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Zoom URL Input */}
        <div className="space-y-2">
          <Label htmlFor="zoom-url">Zoom Meeting URL</Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="zoom-url"
                type="url"
                placeholder="https://zoom.us/j/1234567890"
                value={zoomUrl}
                onChange={(e) => setZoomUrl(e.target.value)}
                className="pl-10"
                disabled={saving}
                data-testid="zoom-url-input"
              />
            </div>
            {currentZoomUrl && (
              <Button data-testid="zoom-link-entry-button-1"
                variant="outline"
                size="icon"
                onClick={() => window.open(currentZoomUrl, '_blank')}
                title="Open in new tab"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Zoom Password Input */}
        <div className="space-y-2">
          <Label htmlFor="zoom-password">Meeting Password (Optional)</Label>
          <Input
            id="zoom-password"
            type="text"
            placeholder="Enter password if required"
            value={zoomPassword}
            onChange={(e) => setZoomPassword(e.target.value)}
            disabled={saving}
            data-testid="zoom-password-input"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-2">
          <Button
            onClick={handleSaveZoomLink}
            disabled={saving || !zoomUrl}
            className="bg-[#0B2240] hover:bg-[#0B2240]/90"
            data-testid="save-zoom-link-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Save Zoom Link
              </>
            )}
          </Button>

          {hasZoomLink && (
            <Button
              variant="outline"
              onClick={handleSendZoomEmail}
              disabled={sending}
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
              data-testid="send-zoom-email-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send to Customer
                </>
              )}
            </Button>
          )}
        </div>

        {/* Email Status */}
        {zoomEmailSentAt && (
          <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-3 rounded-lg">
            <Mail className="h-4 w-4" />
            <span>
              Zoom link sent to {customerEmail}
              {zoomEmailSentCount > 1 && ` (${zoomEmailSentCount} times)`}
            </span>
          </div>
        )}

        {/* Customer Info */}
        <div className="text-sm text-gray-500 pt-2 border-t">
          <div className="flex items-center gap-4">
            <span><strong>Customer:</strong> {customerName}</span>
            <span><strong>Email:</strong> {customerEmail}</span>
          </div>
          {scheduledTime && (
            <div className="flex items-center gap-2 mt-1">
              <Clock className="h-4 w-4" />
              <span><strong>Scheduled:</strong> {scheduledTime}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ZoomLinkEntry;
